import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Zap, LogOut, FileText, TrendingUp, TrendingDown, Award, AlertTriangle,
  Layers, RefreshCw, Mail, MailCheck, BarChart3, ArrowUpRight,
  ArrowDownRight, Minus, ChevronRight, CheckCircle2, Clock
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: BACKEND_URL, withCredentials: true, adapter: 'xhr' });

export default function ReportPage() {
  const { t } = useTranslation();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [report, setReport] = useState(null);
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [reportRes, schedRes] = await Promise.all([
        api.get('/api/reports/performance'),
        api.get('/api/reports/schedule'),
      ]);
      setReport(reportRes.data);
      setSchedule(schedRes.data);
    } catch (err) {
      console.error('Error loading report:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRegenerate = async () => {
    setGenerating(true);
    setFeedback(null);
    try {
      const { data } = await api.get('/api/reports/performance');
      setReport(data);
      setFeedback({ type: 'success', msg: `Report ${data.report_id} generated` });
    } catch {
      setFeedback({ type: 'error', msg: 'Error generating report' });
    } finally {
      setGenerating(false);
    }
  };

  const handleSaveSchedule = async () => {
    if (!schedule) return;
    setSavingSchedule(true);
    setFeedback(null);
    try {
      await api.post('/api/reports/schedule', {
        email_enabled: schedule.email_enabled,
        frequency: schedule.frequency,
        email: schedule.email,
      });
      setFeedback({ type: 'success', msg: schedule.email_enabled ? 'Email reports activados' : 'Email reports desactivados' });
    } catch {
      setFeedback({ type: 'error', msg: 'Error saving schedule' });
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleLogout = async () => { await logout(); navigate('/login'); };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-400 font-mono text-sm">{t('report.loading')}</p>
        </div>
      </div>
    );
  }

  const s = report?.summary || {};
  const alpha = report?.alpha || {};
  const benchmarks = report?.benchmarks || {};

  // Prepare benchmark comparison chart data
  const comparisonData = [
    { name: 'BlackOre X', value: alpha.portfolio_return || 0, fill: '#10b981' },
    ...Object.values(benchmarks).map(b => ({
      name: b.name,
      value: b.return_1m || 0,
      fill: b.return_1m >= 0 ? '#3b82f6' : '#ef4444',
    })),
  ];

  return (
    <div data-testid="report-page">
      <main className="p-4 md:p-6 space-y-6">
        {/* Feedback */}
        {feedback && (
          <div className={`flex items-center gap-2 px-4 py-3 border text-sm font-mono ${
            feedback.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' :
            'bg-red-500/10 border-red-500/30 text-red-400'
          }`}>
            {feedback.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
            {feedback.msg}
          </div>
        )}

        {/* Report Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-black text-white" style={{ fontFamily: 'Chivo' }}>{t('report.title')}</h1>
            <p className="text-slate-400 text-sm font-mono">{report?.report_id} | {report?.period} | {report?.generated_at ? new Date(report.generated_at).toLocaleString() : ''}</p>
          </div>
          <button onClick={handleRegenerate} disabled={generating}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-[#030508] font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-50"
            data-testid="regenerate-report-btn">
            {generating ? <RefreshCw className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
            {generating ? 'GENERATING...' : 'REGENERATE'}
          </button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-[#090B0F] border border-white/10 p-5" data-testid="report-net-worth">
            <p className="text-[10px] text-slate-500 font-mono uppercase mb-1">{t('report.netWorth')}</p>
            <p className="text-2xl font-black text-emerald-400" style={{ fontFamily: 'Chivo' }}>
              ${s.net_worth?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-5">
            <p className="text-[10px] text-slate-500 font-mono uppercase mb-1">{t('report.pnlTotal')}</p>
            <p className={`text-2xl font-black ${s.total_pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`} style={{ fontFamily: 'Chivo' }}>
              {s.total_pnl >= 0 ? '+' : ''}${s.total_pnl?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-5">
            <p className="text-[10px] text-slate-500 font-mono uppercase mb-1">{t('common.return')}</p>
            <p className={`text-2xl font-black ${s.portfolio_return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`} style={{ fontFamily: 'Chivo' }}>
              {s.portfolio_return_pct >= 0 ? '+' : ''}{s.portfolio_return_pct?.toFixed(2)}%
            </p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-5">
            <p className="text-[10px] text-slate-500 font-mono uppercase mb-1">{t('report.activeAgents')}</p>
            <p className="text-2xl font-black text-white" style={{ fontFamily: 'Chivo' }}>{s.num_agents}</p>
          </div>
          <div className="bg-[#090B0F] border border-white/10 p-5">
            <p className="text-[10px] text-slate-500 font-mono uppercase mb-1">{t('common.trades')}</p>
            <p className="text-2xl font-black text-white" style={{ fontFamily: 'Chivo' }}>{s.total_trades}</p>
          </div>
        </div>

        {/* Alpha + Benchmark Comparison */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Alpha Cards */}
          <div className="bg-[#090B0F] border border-white/10 p-6 space-y-4">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
              <Award className="w-4 h-4 text-emerald-500" /> Alpha vs Benchmarks
            </h3>
            <div className="grid grid-cols-2 gap-4">
              <div className={`p-4 border ${alpha.vs_spy >= 0 ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                <p className="text-xs text-slate-400 mb-1">{t('report.alphaSP')}</p>
                <div className="flex items-center gap-2">
                  {alpha.vs_spy >= 0 ? <ArrowUpRight className="w-5 h-5 text-emerald-400" /> : <ArrowDownRight className="w-5 h-5 text-red-400" />}
                  <span className={`text-2xl font-black font-mono ${alpha.vs_spy >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {alpha.vs_spy >= 0 ? '+' : ''}{alpha.vs_spy?.toFixed(2)}%
                  </span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">S&P 500: {alpha.spy_return?.toFixed(2)}%</p>
              </div>
              <div className={`p-4 border ${alpha.vs_btc >= 0 ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-red-500/30 bg-red-500/5'}`}>
                <p className="text-xs text-slate-400 mb-1">{t('report.alphaBTC')}</p>
                <div className="flex items-center gap-2">
                  {alpha.vs_btc >= 0 ? <ArrowUpRight className="w-5 h-5 text-emerald-400" /> : <ArrowDownRight className="w-5 h-5 text-red-400" />}
                  <span className={`text-2xl font-black font-mono ${alpha.vs_btc >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {alpha.vs_btc >= 0 ? '+' : ''}{alpha.vs_btc?.toFixed(2)}%
                  </span>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">BTC: {alpha.btc_return?.toFixed(2)}%</p>
              </div>
            </div>
          </div>

          {/* Benchmark Bar Chart */}
          <div className="bg-[#090B0F] border border-white/10 p-6">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white mb-4 flex items-center gap-2">
              <BarChart3 className="w-4 h-4 text-emerald-500" /> 1 Month Return: Your Portfolio vs Market
            </h3>
            <div className="h-[200px]" data-testid="benchmark-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparisonData} layout="vertical" margin={{ left: 80 }}>
                  <XAxis type="number" tick={{ fill: '#64748b', fontSize: 10, fontFamily: 'monospace' }}
                    tickFormatter={v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}%`} axisLine={false} tickLine={false} />
                  <YAxis type="category" dataKey="name" tick={{ fill: '#e2e8f0', fontSize: 11, fontFamily: 'monospace' }}
                    axisLine={false} tickLine={false} width={75} />
                  <Tooltip
                    contentStyle={{ background: '#11141D', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 0, fontFamily: 'monospace', fontSize: 12 }}
                    formatter={(v) => [`${v >= 0 ? '+' : ''}${v.toFixed(2)}%`, 'Return']}
                  />
                  <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                    {comparisonData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fill} fillOpacity={0.8} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Top & Worst Agent */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {report?.best_agent && (
            <div className="bg-[#090B0F] border border-emerald-500/20 p-6" data-testid="best-agent-card">
              <div className="flex items-center gap-2 mb-3">
                <Award className="w-5 h-5 text-emerald-500" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-400">{t('report.topPerformer')}</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl font-bold text-white">{report.best_agent.agent_id}</p>
                  <p className="text-sm text-slate-400">{report.best_agent.agent_name}</p>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-black font-mono ${report.best_agent.return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {report.best_agent.return_pct >= 0 ? '+' : ''}{report.best_agent.return_pct?.toFixed(2)}%
                  </p>
                  <p className="text-xs text-slate-500">Win Rate: {report.best_agent.win_rate?.toFixed(1)}% | Trades: {report.best_agent.total_trades}</p>
                </div>
              </div>
            </div>
          )}
          {report?.worst_agent && (
            <div className="bg-[#090B0F] border border-red-500/20 p-6" data-testid="worst-agent-card">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-red-400">{t('report.needsAttention')}</span>
              </div>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xl font-bold text-white">{report.worst_agent.agent_id}</p>
                  <p className="text-sm text-slate-400">{report.worst_agent.agent_name}</p>
                </div>
                <div className="text-right">
                  <p className={`text-2xl font-black font-mono ${report.worst_agent.return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {report.worst_agent.return_pct >= 0 ? '+' : ''}{report.worst_agent.return_pct?.toFixed(2)}%
                  </p>
                  <p className="text-xs text-slate-500">Win Rate: {report.worst_agent.win_rate?.toFixed(1)}% | Trades: {report.worst_agent.total_trades}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Full Agent Breakdown */}
        <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('report.detailByAgent')}</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-slate-500 text-xs font-mono uppercase">
                  <th className="text-left p-3">{t('common.agent')}</th>
                  <th className="text-left p-3">{t('common.market')}</th>
                  <th className="text-right p-3">{t('common.invested')}</th>
                  <th className="text-right p-3">{t('report.currentValue')}</th>
                  <th className="text-right p-3">P&L</th>
                  <th className="text-right p-3">{t('common.return')}</th>
                  <th className="text-right p-3">{t('common.winRate')}</th>
                  <th className="text-right p-3">{t('common.trades')}</th>
                </tr>
              </thead>
              <tbody>
                {report?.agents?.map(a => (
                  <tr key={a.agent_id} className="border-b border-white/5 hover:bg-white/5">
                    <td className="p-3 font-mono font-bold text-white">{a.agent_id} <span className="text-slate-500 text-xs font-normal">{a.agent_name}</span></td>
                    <td className="p-3 text-xs"><span className="px-2 py-0.5 bg-white/10 text-slate-300">{a.market_type || '-'}</span></td>
                    <td className="p-3 text-right text-white">${a.invested?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className="p-3 text-right text-white">${a.current_value?.toLocaleString('en-US', { minimumFractionDigits: 2 })}</td>
                    <td className={`p-3 text-right font-mono font-bold ${a.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {a.pnl >= 0 ? '+' : ''}${a.pnl?.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                    </td>
                    <td className={`p-3 text-right font-mono ${a.return_pct >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {a.return_pct >= 0 ? '+' : ''}{a.return_pct?.toFixed(2)}%
                    </td>
                    <td className="p-3 text-right font-mono text-white">{a.win_rate?.toFixed(1)}%</td>
                    <td className="p-3 text-right font-mono text-slate-400">{a.total_trades}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Benchmark Details */}
        <div className="bg-[#090B0F] border border-white/10 overflow-hidden">
          <div className="p-4 border-b border-white/10">
            <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white">{t('report.marketBenchmarks')}</h3>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0">
            {Object.values(benchmarks).map(b => (
              <div key={b.symbol} className="p-4 border-r border-b border-white/5 last:border-r-0">
                <p className="text-xs text-slate-500 font-mono mb-1">{b.name}</p>
                <p className="text-lg font-bold text-white">${b.current_price?.toLocaleString()}</p>
                <p className={`text-sm font-mono ${b.return_1m >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                  {b.return_1m >= 0 ? '+' : ''}{b.return_1m?.toFixed(2)}%
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* Email Schedule */}
        {schedule && (
          <div className="bg-[#090B0F] border border-white/10 p-6" data-testid="email-schedule-section">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm uppercase tracking-[0.2em] font-semibold text-white flex items-center gap-2">
                <Mail className="w-4 h-4 text-emerald-500" /> Email Report Automatico
              </h3>
              <button
                onClick={() => setSchedule({ ...schedule, email_enabled: !schedule.email_enabled })}
                className={`flex items-center gap-2 px-4 py-2 text-sm font-bold transition-all ${
                  schedule.email_enabled
                    ? 'bg-emerald-500 text-[#030508]'
                    : 'bg-white/10 text-slate-400'
                }`}
                data-testid="email-toggle-btn"
              >
                {schedule.email_enabled ? <MailCheck className="w-4 h-4" /> : <Mail className="w-4 h-4" />}
                {schedule.email_enabled ? 'ACTIVADO' : 'DESACTIVADO'}
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="text-xs text-slate-400 font-mono uppercase block mb-2">{t('common.email')}</label>
                <input
                  type="email"
                  value={schedule.email || ''}
                  onChange={e => setSchedule({ ...schedule, email: e.target.value })}
                  className="w-full bg-[#11141D] border border-white/10 text-white px-4 py-2 text-sm font-mono focus:outline-none focus:border-emerald-500/50"
                  data-testid="schedule-email-input"
                />
              </div>
              <div>
                <label className="text-xs text-slate-400 font-mono uppercase block mb-2">{t('common.frequency')}</label>
                <div className="flex gap-2">
                  {['weekly', 'biweekly', 'monthly'].map(f => (
                    <button key={f}
                      onClick={() => setSchedule({ ...schedule, frequency: f })}
                      className={`px-3 py-2 text-xs font-mono transition-all ${
                        schedule.frequency === f
                          ? 'bg-emerald-500 text-[#030508] font-bold'
                          : 'bg-white/10 text-slate-400'
                      }`}
                      data-testid={`sched-freq-${f}`}
                    >
                      {f === 'weekly' ? 'Weekly' : f === 'biweekly' ? 'Biweekly' : 'Monthly'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-end">
                <button
                  onClick={handleSaveSchedule}
                  disabled={savingSchedule}
                  className="flex items-center gap-2 px-6 py-2 bg-emerald-500 text-[#030508] font-bold text-sm hover:bg-emerald-400 transition-colors disabled:opacity-50 w-full justify-center"
                  data-testid="save-schedule-btn"
                >
                  {savingSchedule ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                  GUARDAR
                </button>
              </div>
            </div>
            {!schedule.email_enabled && (
              <p className="text-xs text-slate-600 mt-3 font-mono">
                Nota: El envio de emails requiere una API key de Resend o SMTP configurado.
              </p>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
